import { useState } from 'react'

import { ToggleGroup } from '@/components/toggle-group/toggleGroup'
import { Typography } from '@/components/typography/typography'
import { useLocationQuery } from '@/features/url-handling'
import { cn } from '@/libs/tw'
import { useAppConfigStore } from '@/stores/app-store-config/appConfigStore'
import { useCartStore } from '@/stores/cart-store/cartStore'
import { useStepStore } from '@/stores/step-store/stepStore'
import { translate } from '@/utils/translator'

import { useCantBookOnline } from '../hooks/useCantBookOnline'
import { useLocations } from '../hooks/useLocations'
import { Location } from '../types'
import { LocationCard } from './locationCard'

const getUniqTypesAndCategories = (locations?: Location[]) => {
	const categories = new Set<string>()
	const types = new Set<string>()
	locations?.forEach((location) => {
		if (location.classification) {
			types.add(location.classification?.name)
		}

		location.categories.forEach((category) => categories.add(category))
	})

	return {
		categories: Array.from(categories),
		types: Array.from(types),
	}
}

export const LocationContent = () => {
	useLocationQuery()
	const allLabel = translate('all')
	const isAllSelected = (value: string) => value === allLabel
	const locationFilterDisabled = useAppConfigStore.use.locationFilterDisabled?.()
	const setNextStep = useStepStore.use.setNextStep()
	const setLocation = useCartStore.use.setLocation()
	const { data: locations } = useLocations()

	const [typeFilter, setTypeFilter] = useState<string>(allLabel)
	const [categoryFilter, setCategoryFilter] = useState<string>(allLabel)

	const { CantBeBookedPopup, cantBeBooked } = useCantBookOnline()

	const { types, categories } = getUniqTypesAndCategories(locations)

	const filteredLocations = locations?.filter((location) => {
		if (isAllSelected(typeFilter) && isAllSelected(categoryFilter)) return true

		if (isAllSelected(typeFilter)) return location.categories.includes(categoryFilter!)
		if (isAllSelected(categoryFilter)) return location.classification?.name === typeFilter

		return (
			location.classification?.name === typeFilter &&
			location.categories.includes(categoryFilter)
		)
	})

	const handleLocationSelection = (hotelCode: string) => {
		const selectedLocation = locations?.find((location) => location.hotelCode === hotelCode)

		if (cantBeBooked(hotelCode)) return

		setLocation(selectedLocation)
		setNextStep()
	}

	const onTypeFilterSelect = (value?: string) => {
		if (value) setTypeFilter(value)
	}

	const onCategoryFilterSelect = (value?: string) => {
		if (value) setCategoryFilter(value)
	}
	return (
		<>
			<CantBeBookedPopup />
			<div className={cn('flex flex-col gap-40')}>
				<div
					className={cn(
						'flex flex-col gap-24 text-locationCard-text',
						'md:flex-row md:gap-56 '
					)}
				>
					{!locationFilterDisabled && types.length > 1 && (
						<div className={cn('flex flex-col items-start gap-16 text-textColor')}>
							<Typography style="p-bold" element="h3">
								{translate('location-types-filter-label')}
							</Typography>
							<ToggleGroup
								description="type filter"
								value={typeFilter}
								items={[allLabel, ...types]}
								onSelect={onTypeFilterSelect}
							/>
						</div>
					)}
					{!locationFilterDisabled && categories.length > 0 && (
						<div className={cn('flex flex-col items-start gap-16 text-textColor')}>
							<Typography style="p-bold" element="h3">
								{translate('location-destination-filter-label')}
							</Typography>
							<ToggleGroup
								description="category filter"
								value={categoryFilter}
								items={[allLabel, ...categories]}
								onSelect={onCategoryFilterSelect}
							/>
						</div>
					)}
				</div>
				<div
					className={cn(
						'flex flex-col gap-32',
						'md:grid md:grid-cols-3 md:gap-24 lg:grid-cols-4 xl:grid-cols-5'
					)}
					data-testid="locations"
				>
					{filteredLocations &&
						filteredLocations.map((location) => (
							<LocationCard
								key={location.hotelCode}
								title={location.title}
								hotelCode={location.hotelCode}
								classification={location.classification}
								image={location.image}
								onSelect={handleLocationSelection}
							/>
						))}
				</div>
			</div>
		</>
	)
}
